export interface ParsedSlide {
  number: number;
  label: string;
  title: string;
  body: string;
  isCover: boolean;
  isCTA: boolean;
}

export interface ParsedCarousel {
  carouselTitle: string;
  slides: ParsedSlide[];
  postDescription: string;
}

const COVER_KEYWORDS = ["okładka", "cover", "hook"];
const CTA_KEYWORDS = ["cta", "call to action", "wezwanie"];
const MIN_SLIDES_FOR_VALID_CAROUSEL = 2;

const TITLE_PATTERNS: ReadonlyArray<RegExp> = [
  /\*{0,2}\s*📱?\s*(?:KARUZELA|CAROUSEL)\s*[:：—–-]\s*(.+?)\*{0,2}\s*$/im,
  /^\s*#{1,4}\s*📱?\s*(?:KARUZELA|CAROUSEL)\s*[:：—–-]?\s*(.+)$/im,
  /^\s*📱\s*(.+)$/m,
];

const SLIDE_HEADER_REGEX =
  /(?:^|\n)\s*(?:#{1,4}\s*)?\*{0,2}\s*(?:Slajd|Slide|Slayd|Slajdów|Slide'ów)\s+(\d+)\s*(?:[:：—–-]\s*([^\n*#]+?))?\s*\*{0,2}\s*[:：]?\s*(?=\n|$)/gi;

const FALLBACK_SLIDE_REGEX =
  /(?:^|\n)\s*(?:#{1,4}\s*)?\*{0,2}\s*(?:Slajd|Slide)?\s*(\d+)\s*[\.\)\]]\s*([^\n*#]+)?\*{0,2}/gi;

const POST_DESC_REGEX = /(?:📝\s*)?OPIS(?:\s+DO\s+POSTA)?\s*[:：]?\s*\n([\s\S]*?)$/i;
const POST_DESC_BOUNDARY_REGEX = /\n\s*(?:📝\s*)?OPIS(?:\s+DO\s+POSTA)?\s*[:：]/i;

const COVER_KEYWORD_SET: ReadonlySet<string> = new Set(COVER_KEYWORDS);
const CTA_KEYWORD_SET: ReadonlySet<string> = new Set(CTA_KEYWORDS);

function detectByKeywords(label: string, keywords: ReadonlySet<string>): boolean {
  const lower = label.toLowerCase();
  for (const kw of keywords) {
    if (lower.includes(kw)) return true;
  }
  return false;
}

function stripCodeFences(text: string): string {
  return text.replace(/```[a-z]*\n?/gi, "").replace(/```/g, "");
}

function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/^#{1,4}\s+/gm, "")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/~~(.*?)~~/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^[-•]\s+/gm, "")
    .trim();
}

function extractTitle(text: string): string | null {
  for (const pattern of TITLE_PATTERNS) {
    const m = text.match(pattern);
    if (!m?.[1]) continue;
    const title = stripMarkdown(m[1])
      .replace(/[*#]+$/g, "")
      .trim();
    if (title.length > 0 && title.length < 200) return title;
  }
  return null;
}

interface SlideMatch {
  number: number;
  label: string;
  startIndex: number;
  contentStart: number;
}

function findSlideHeaders(text: string): SlideMatch[] {
  const matches: SlideMatch[] = [];
  SLIDE_HEADER_REGEX.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = SLIDE_HEADER_REGEX.exec(text)) !== null) {
    matches.push({
      number: parseInt(m[1], 10),
      label: (m[2] || "").trim(),
      startIndex: m.index,
      contentStart: m.index + m[0].length,
    });
  }

  if (matches.length === 0) {
    FALLBACK_SLIDE_REGEX.lastIndex = 0;
    while ((m = FALLBACK_SLIDE_REGEX.exec(text)) !== null) {
      matches.push({
        number: parseInt(m[1], 10),
        label: (m[2] || "").trim(),
        startIndex: m.index,
        contentStart: m.index + m[0].length,
      });
    }
  }

  // Ensure slides are sorted by number to avoid weird jumps
  return matches.sort((a, b) => a.number - b.number);
}

function findContentEnd(text: string, currentEnd: number, nextStart: number | null): number {
  if (nextStart !== null) return nextStart;
  const tail = text.slice(currentEnd);
  const opisMatch = tail.match(POST_DESC_BOUNDARY_REGEX);
  return opisMatch && opisMatch.index !== undefined ? currentEnd + opisMatch.index : text.length;
}

function buildSlideFromMatch(text: string, match: SlideMatch, end: number): ParsedSlide {
  const rawContent = text.slice(match.contentStart, end).trim();
  const lines = rawContent
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const title = stripMarkdown(lines[0] || "")
    .replace(/^Nagłówek\s*[:：]?\s*/i, "")
    .trim();
  const body = stripMarkdown(lines.slice(1).join("\n"))
    .replace(/^Treść\s*[:：]?\s*/i, "")
    .trim();

  return {
    number: match.number,
    label: match.label,
    title,
    body,
    isCover: match.number === 1 || detectByKeywords(match.label, COVER_KEYWORD_SET),
    isCTA: detectByKeywords(match.label, CTA_KEYWORD_SET),
  };
}

function extractSlides(text: string): ParsedSlide[] {
  const headers = findSlideHeaders(text);
  if (headers.length === 0) return [];

  return headers.map((header, i) => {
    const nextStart = i + 1 < headers.length ? headers[i + 1].startIndex : null;
    const end = findContentEnd(text, header.contentStart, nextStart);
    return buildSlideFromMatch(text, header, end);
  });
}

function extractPostDescription(text: string): string {
  const m = text.match(POST_DESC_REGEX);
  if (!m?.[1]) return "";
  return stripMarkdown(m[1].trim());
}

function deriveTitleFromFirstSlide(slides: ParsedSlide[]): string {
  const first = slides[0];
  if (!first) return "Karuzela";
  const candidate = (first.title || first.label || "").trim();
  return candidate.length > 0 ? candidate : "Karuzela";
}

export function parseCarouselResponse(text: string): ParsedCarousel | null {
  if (!text || typeof text !== "string") return null;

  const cleaned = stripCodeFences(text);
  const slides = extractSlides(cleaned);

  if (slides.length < MIN_SLIDES_FOR_VALID_CAROUSEL) return null;

  const explicitTitle = extractTitle(cleaned);
  const carouselTitle = explicitTitle || deriveTitleFromFirstSlide(slides);
  const postDescription = extractPostDescription(cleaned);

  return { carouselTitle, slides, postDescription };
}
